/**
 * SHER-208 — when a survey field is deleted/replaced in the EDC editor it is
 * soft-deleted and disappears from the live survey (getSurvey returns only the
 * current fields). Submissions created against that field still hold a
 * SurveyRecord for it. The edit form (FillSurvey) is built only from the live
 * survey, so those records were invisible while editing and were lost — or
 * shadowed by a blank replacement field — on save.
 *
 * The submission payload still carries the record (and its original field), so
 * we surface those records here as read-only "archived" fields appended to the
 * edit form: the historical value stays visible and round-trips unchanged on
 * save (updateSubmission updates records present in the payload, leaving the
 * rest intact).
 *
 * An orphan is detected by FIELD IDENTITY — the record's field id is no longer
 * in the live survey — NOT by the field's `isActive` flag: the submission
 * payload reports `isActive: true` even for soft-deleted fields, so that flag
 * is unreliable here. Detection is scoped to the section(s) being edited so a
 * still-live field belonging to another (un-edited) section is not mistaken
 * for an orphan.
 */

interface SurveyFieldLike {
    id: any;
    name?: string;
    label?: string;
    type?: string;
    [key: string]: any;
}
interface SurveyRecordLike {
    surveyField?: SurveyFieldLike;
    surveySection?: { id?: any; [key: string]: any };
    value?: any;
    [key: string]: any;
}
interface SectionLike {
    id?: any;
    fields?: SurveyFieldLike[];
    [key: string]: any;
}
interface InitDataLike {
    surveyRecords?: SurveyRecordLike[];
    [key: string]: any;
}

export const ARCHIVED_SECTION_TITLE_ID = 'archived';

/**
 * Returns the extra fields to append to an edit form: a `title_section`
 * separator followed by one read-only field per record whose field is no
 * longer in the live survey. Returns `[]` when there are none (the common case).
 */
export function getArchivedFields(
    sectionsSurvey: SectionLike[] | undefined,
    initData: InitDataLike | undefined | null,
): any[] {
    if (!initData || !Array.isArray(initData.surveyRecords)) {
        return [];
    }

    const liveFieldIds = new Set<any>();
    const editedSectionIds = new Set<any>();
    for (const section of sectionsSurvey || []) {
        if (section && section.id != null) {
            editedSectionIds.add(section.id);
        }
        for (const field of section?.fields || []) {
            if (field && field.id != null) {
                liveFieldIds.add(field.id);
            }
        }
    }

    const orphanById = new Map<any, any>();
    for (const record of initData.surveyRecords) {
        const field = record && record.surveyField;
        if (!field || field.id == null) {
            continue;
        }
        if (liveFieldIds.has(field.id) || orphanById.has(field.id)) {
            continue;
        }
        // Scope to the section(s) being edited: a record on a field that simply
        // belongs to another, un-edited section is not an orphan of this edit.
        const recordSectionId = record.surveySection && record.surveySection.id;
        if (editedSectionIds.size > 0 && recordSectionId != null
            && !editedSectionIds.has(recordSectionId)) {
            continue;
        }
        orphanById.set(field.id, {
            ...field,
            label: field.label || field.name,
            archived: true,
            required: false,
            validation: undefined,
        });
    }

    if (orphanById.size === 0) {
        return [];
    }

    const archivedTitle = {
        type: 'title_section',
        label: 'hospital.archived-fields-section',
        name: 'title_section_' + ARCHIVED_SECTION_TITLE_ID,
        required: false,
        validation: 'notEmpty',
        id: ARCHIVED_SECTION_TITLE_ID,
    };

    return [archivedTitle, ...orphanById.values()];
}
